import React from "react";
import { useMsal } from "@azure/msal-react";
import { useSearchParams } from "react-router-dom";
import { getReviews, getReviewStatus } from "../api/reviewApi";
import { getPublicConfig } from "../api/publicConfigApi";
import { ReviewRow, ReviewStatus, ReviewDefaults } from "../types/review";
import { getToken } from "../api/auth";
import MemberRow from "../components/MemberRow";
import ReviewHeader from "../components/ReviewHeader";
import { useSlowLoadNotice } from "../hooks/useSlowLoadNotice";

function formatCycleLabel(cycleName: string, frequency: string): string {
  const match = /^(\d{4})Q([1-4])$/.exec(cycleName);
  if (!match) return cycleName;
  const year = match[1];
  const quarter = parseInt(match[2]!, 10);
  const months = ["January", "April", "July", "October"];
  const month = months[quarter - 1];
  switch (frequency) {
    case "Annual":     return `${month} ${year} Annual Review`;
    case "SemiAnnual": return `${month} ${year} Semi-Annual Review`;
    default:           return `Q${quarter} ${year} Quarterly Review`;
  }
}

// Fixed column widths shared across all group tables for consistent alignment.
// Was 8 columns including a redundant "Access" column showing groupName;
// since the table is grouped by groupName as a section header, that column
// was dropped in the Phase 3 camelCase migration.
// Flags: four fixed 24px icon slots; last column holds the save status / Retry.
const COL_WIDTHS = ["15%", "19%", "13%", "8%", "14%", "23%", "8%"];

const ReviewPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [searchParams] = useSearchParams();

  const toolName   = searchParams.get("tool")  ?? "";
  const cycleName  = searchParams.get("cycle") ?? "";
  const stage      = (searchParams.get("stage") === "2" ? 2 : 1) as 1 | 2;
  const frequency  = searchParams.get("freq")  ?? "Quarterly";
  const cycleLabel = formatCycleLabel(cycleName, frequency);

  const [closed,  setClosed]  = React.useState<boolean>(false);
  const [rows,    setRows]    = React.useState<ReviewRow[]>([]);
  const [status,  setStatus]  = React.useState<ReviewStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState<string | null>(null);
  // Default justifications fetched at mount from /api/config/public. Empty
  // strings until loaded — MemberRow handles empties by leaving the field blank.
  const [defaults, setDefaults] = React.useState<ReviewDefaults>({
    approveDefaultJustification: "",
    denyDefaultJustification:    "",
  });

  const account             = accounts[0];
  const reviewerUpn         = account?.username ?? "";
  const reviewerDisplayName = account?.name     ?? "";

  // ── Derive all counts from local rows state (no stale-closure bugs) ───────
  // F27: a decision counts as "reviewed" only when it's Approve/Deny WITH a
  // non-blank justification. A blank-justification Approve/Deny is treated as
  // not reviewed (escalates at S1 / undecided at S2) — matching the backend.
  const countsAsReviewed = React.useCallback((r: ReviewRow): boolean => {
    const dec  = stage === 1 ? r.s1Decision : r.s2Decision;
    const just = stage === 1 ? r.s1Justification : r.s2Justification;
    return (dec === "Approve" || dec === "Deny") && !!just && just.trim() !== "";
  }, [stage]);

  const reviewedCount = React.useMemo(() =>
    rows.filter(countsAsReviewed).length,
    [rows, countsAsReviewed]
  );
  const escalatedCount = React.useMemo(() =>
    rows.filter((r) => {
      const justified = (r.s1Decision === "Approve" || r.s1Decision === "Deny")
        && !!r.s1Justification && r.s1Justification.trim() !== "";
      return !justified; // NULL, Don't Know, or unjustified Approve/Deny
    }).length,
    [rows]
  );
  const pendingCount = rows.length - reviewedCount;

  // ── Group rows by Entra group ─────────────────────────────────────────────
  const grouped = React.useMemo(() => {
    const map = new Map<string, ReviewRow[]>();
    for (const row of rows) {
      const group = row.groupName ?? "Unknown Group";
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(row);
    }
    return map;
  }, [rows]);

  // ── Load review data on mount ─────────────────────────────────────────────
  React.useEffect(() => {
    if (!toolName || !cycleName || !account) return;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        await getToken(instance, account);
        const [fetchedRows, fetchedStatus, fetchedDefaults] = await Promise.all([
          getReviews(cycleName, toolName, stage),
          getReviewStatus(cycleName, toolName),
          getPublicConfig(),
        ]);
        setRows(fetchedRows);
        setStatus(fetchedStatus);
        setDefaults(fetchedDefaults);
        const isClosed = stage === 1 ? fetchedStatus.stage1Closed : fetchedStatus.stage2Closed;
        if (isClosed) setClosed(true);
      } catch (err: any) {
        const httpStatus = err?.response?.status;
        if (httpStatus === 401 || httpStatus === 403) {
          setError("You are not the designated reviewer for this access review.");
        } else {
          setError("Failed to load review data. Please refresh the page.");
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [toolName, cycleName, stage, account, instance]);

  // ── Poll status every 30 s — detect deadline passing while tab is open ────
  React.useEffect(() => {
    if (!toolName || !cycleName || !account || closed) return;
    const interval = setInterval(async () => {
      try {
        const s = await getReviewStatus(cycleName, toolName);
        const isClosed = stage === 1 ? s.stage1Closed : s.stage2Closed;
        if (isClosed) setClosed(true);
      } catch { /* ignore poll errors */ }
    }, 30_000);
    return () => clearInterval(interval);
  }, [toolName, cycleName, stage, account, closed]);

  // ── Row update callback ───────────────────────────────────────────────────
  function handleRowUpdated(updatedRow: ReviewRow) {
    setRows((prev) => prev.map((r) => r.id === updatedRow.id ? updatedRow : r));
  }

  // Surface a "waking up" notice if the initial load runs long — the Serverless
  // SQL DB may be resuming from auto-pause on the first request of a session
  // (no warmup timer by design, F35). Hook must run before the early returns.
  const wakingUp = useSlowLoadNotice(loading);

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!toolName || !cycleName) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h2>Invalid link</h2>
        <p>This review link is missing required parameters. Please use the link from your Slack notification.</p>
      </div>
    );
  }

  if (closed) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "100vh",
        fontFamily: "Segoe UI, sans-serif", background: "#f9f9f9",
      }}>
        <div style={{
          background: "white", border: "1px solid #ddd", borderRadius: "8px",
          padding: "48px 56px", textAlign: "center", maxWidth: "480px",
        }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <h2 style={{ margin: "0 0 12px", color: "#323130" }}>Review Closed</h2>
          <p style={{ color: "#605e5c", margin: 0 }}>
            The deadline for the <strong>{toolName}</strong> <strong>{cycleLabel}</strong> has passed.
            No further changes can be made. You can close this tab.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <p>Loading review for <strong>{toolName}</strong>…</p>
        {wakingUp && (
          <p style={{ color: "#605e5c", fontSize: "13px", marginTop: "12px" }}>
            Waking the service up after a period of inactivity — this can take up to a minute. Please hold on…
          </p>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#d13438" }}>
        <p>{error}</p>
      </div>
    );
  }

  const isPending = (row: ReviewRow) => {
    if (stage === 1 && row.isSelfReview) return false; // auto-escalated, not the reviewer's action
    // Pending unless it counts as reviewed (Approve/Deny WITH justification).
    // F27: a blank-justification Approve/Deny stays pending/highlighted.
    return !countsAsReviewed(row);
  };

  const headers = ["Name", "UPN", "Team", "Flags", "Decision", "Justification", ""];

  return (
    <div style={{ padding: "24px", fontFamily: "Segoe UI, sans-serif", maxWidth: "1400px", margin: "0 auto" }}>

      {status && (
        <ReviewHeader
          status={{
            ...status,
            total:     stage === 2 ? rows.length : status.total,
            reviewed:  reviewedCount,
            escalated: escalatedCount,
            pending:   pendingCount,
          }}
          stage={stage}
          cycleLabel={cycleLabel}
        />
      )}

      {/* ── Real-time save info bar ─────────────────────────────────────── */}
      <div style={{
        background: "#eff6fc", border: "1px solid #c7e0f4", borderRadius: "4px",
        padding: "10px 16px", marginBottom: "24px",
        display: "flex", alignItems: "center", gap: "10px",
        fontSize: "13px", color: "#004578",
      }}>
        <span style={{ fontSize: "16px" }}>ℹ️</span>
        <span>Decisions are saved in real time. Once you have finished reviewing all members, simply close this tab.</span>
      </div>

      {rows.length === 0 ? (
        <p>No members to review.</p>
      ) : (
        Array.from(grouped.entries()).map(([groupName, groupRows]) => {
          const groupPending = groupRows.filter(isPending).length;
          return (
            <div key={groupName} style={{ marginBottom: "40px" }}>
              <h3 style={{
                borderBottom: "2px solid #0078d4", paddingBottom: "8px",
                color: "#0078d4", display: "flex", alignItems: "center", gap: "10px",
              }}>
                {groupName}
                {groupPending > 0 && (
                  <span style={{
                    background: "#d13438", color: "white",
                    borderRadius: "12px", padding: "1px 8px",
                    fontSize: "13px", fontWeight: 600,
                  }}>
                    {groupPending} pending
                  </span>
                )}
              </h3>
              <table style={{
                width: "100%", borderCollapse: "collapse", fontSize: "14px",
                tableLayout: "fixed",  // forces consistent column widths across all tables
              }}>
                <colgroup>
                  {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
                </colgroup>
                <thead>
                  <tr style={{ background: "#f3f3f3", textAlign: "left" }}>
                    {headers.map((h, i) => (
                      <th key={i} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map((row) => (
                    <MemberRow
                      key={row.id}
                      row={row}
                      stage={stage}
                      cycleName={cycleName}
                      toolName={toolName}
                      reviewerUpn={reviewerUpn}
                      reviewerDisplayName={reviewerDisplayName}
                      defaults={defaults}
                      onUpdated={handleRowUpdated}
                      highlight={isPending(row)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
};

const th: React.CSSProperties = {
  padding: "10px 12px",
  fontWeight: 600,
  borderBottom: "1px solid #ddd",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export default ReviewPage;
